<section class="d-flex flex-column gap-2" data-testid="onboardings-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.onboardings.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.onboardings.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="onboardings-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="onboardings-scope-note">
    {{ 'backoffice.onboardings.scope_note' | translate }}
  </p>

  @if (!facade.canCheckHealth()) {
    <p class="av-meta m-0" data-testid="onboardings-restricted">
      {{ 'backoffice.onboardings.health.restricted' | translate }}
    </p>
  }
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="onboardings-summary">
  <h3 class="av-section-title m-0">{{ 'backoffice.onboardings.summary_title' | translate }}</h3>

  <div class="d-flex flex-wrap gap-4">
    @for (option of statusOptions; track option) {
      <div class="d-flex flex-column gap-1">
        <span class="av-meta">{{ statusLabelKeys[option] | translate }}</span>
        <span class="cell-strong" [attr.data-testid]="'funnel-' + option">
          {{ facade.summaryFor(option) }}
        </span>
      </div>
    }
  </div>
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="tenant-health-panel">
  <div class="d-flex flex-column gap-1">
    <h3 class="av-section-title m-0">{{ 'backoffice.onboardings.health.title' | translate }}</h3>
    <p class="av-meta m-0">{{ 'backoffice.onboardings.health.hint' | translate }}</p>
  </div>

  @if (facade.lastCheck(); as check) {
    <div class="d-flex flex-wrap gap-4" data-testid="tenant-health-result">
      <div class="d-flex flex-column gap-1">
        <span class="av-meta">{{ 'backoffice.onboardings.health.status' | translate }}</span>
        <av-status-chip
          [label]="healthLabelKey(check.status) | translate"
          [tone]="healthTone(check.status)"
        ></av-status-chip>
      </div>

      <div class="d-flex flex-column gap-1">
        <span class="av-meta">{{ 'backoffice.onboardings.health.error_code' | translate }}</span>
        <span class="cell-strong">{{ check.error_code || unresolved }}</span>
      </div>

      <div class="d-flex flex-column gap-1">
        <span class="av-meta">{{ 'backoffice.onboardings.health.checked_at' | translate }}</span>
        <span class="cell-strong">
          {{ check.checked_at ? (check.checked_at | date: dateTimeFormat) : unresolved }}
        </span>
      </div>

      <div class="d-flex flex-column gap-1">
        <span class="av-meta">{{ 'backoffice.onboardings.health.registration' | translate }}</span>
        <span class="av-source">{{ check.registration_id || unresolved }}</span>
      </div>
    </div>
  } @else {
    <p class="av-meta m-0" data-testid="tenant-health-no-result">
      {{ 'backoffice.onboardings.health.no_result' | translate }}
    </p>
  }

  <div class="d-flex flex-column gap-1">
    <h4 class="av-section-title m-0">{{ 'backoffice.onboardings.reconsent.title' | translate }}</h4>
    <p class="av-meta m-0">{{ 'backoffice.onboardings.reconsent.hint' | translate }}</p>
    <p class="av-meta m-0" data-testid="reconsent-pending-note">
      {{ 'backoffice.onboardings.reconsent.pending_note' | translate }}
    </p>
  </div>
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="onboardings-body">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.onboardings.error.title' | translate"
        [message]="'backoffice.onboardings.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <div class="filter-row d-flex flex-wrap align-items-end gap-2">
        <label class="d-flex flex-column gap-1" for="onboarding-status">
          <span class="av-meta">{{ 'backoffice.onboardings.filters.status' | translate }}</span>
          <select
            id="onboarding-status"
            class="av-field"
            [value]="facade.status()"
            (change)="filterStatus($any($event.target).value)"
            data-testid="onboarding-status-filter"
          >
            <option value="">
              {{ 'backoffice.onboardings.filters.any_status' | translate }}
            </option>
            @for (option of statusOptions; track option) {
              <option [value]="option">{{ statusLabelKeys[option] | translate }}</option>
            }
          </select>
        </label>
      </div>

      @if (isEmpty()) {
        <av-empty-state
          icon="how_to_reg"
          [title]="
            (facade.status()
              ? 'backoffice.onboardings.empty.filtered_title'
              : 'backoffice.onboardings.empty.title'
            ) | translate
          "
          [message]="
            (facade.status()
              ? 'backoffice.onboardings.empty.filtered_message'
              : 'backoffice.onboardings.empty.message'
            ) | translate
          "
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="onboarding-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.onboardings.table.organization' | translate }}</th>
                <th scope="col">{{ 'backoffice.onboardings.table.status' | translate }}</th>
                <th scope="col">{{ 'backoffice.onboardings.table.contact' | translate }}</th>
                <th scope="col">{{ 'backoffice.onboardings.table.tenant' | translate }}</th>
                <th scope="col">{{ 'backoffice.onboardings.table.consent' | translate }}</th>
                <th scope="col">{{ 'backoffice.onboardings.table.started' | translate }}</th>
                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              @for (entry of facade.onboardings(); track entry.id) {
                <tr>
                  <td class="cell-strong">{{ entry.organizationName }}</td>

                  <td>
                    <av-status-chip
                      [label]="entry.statusLabelKey | translate"
                      [tone]="entry.statusTone"
                    ></av-status-chip>
                  </td>

                  <td class="cell-muted">{{ entry.contactEmail }}</td>

                  <td class="av-source">{{ entry.tenant_id || unresolved }}</td>

                  <td class="cell-muted" data-testid="onboarding-consent">
                    {{
                      entry.hasConsent
                        ? (entry.admin_consent_granted_at | date: dateTimeFormat)
                        : ('backoffice.onboardings.table.not_granted' | translate)
                    }}
                  </td>

                  <td class="cell-muted">
                    {{ entry.created_at ? (entry.created_at | date: dateTimeFormat) : unresolved }}
                  </td>

                  <td>
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="av-btn"
                        [disabled]="!facade.canCheckHealth() || entry.organization === null"
                        (click)="checkHealth(entry)"
                        data-testid="onboarding-check-health"
                      >
                        {{ 'backoffice.onboardings.health.button' | translate }}
                      </button>

                      <button
                        type="button"
                        class="av-btn"
                        [disabled]="!entry.canReconsent"
                        [matTooltip]="
                          entry.canReconsent
                            ? consentUrl(entry)
                            : ('backoffice.onboardings.reconsent.unavailable' | translate)
                        "
                        (click)="openConsent(entry)"
                        data-testid="onboarding-reconsent"
                      >
                        {{ 'backoffice.onboardings.reconsent.button' | translate }}
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.onboardings.loading.title' | translate"
        [message]="'backoffice.onboardings.loading.message' | translate"
      ></av-empty-state>
    }
  }
</section>
